import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnInit } from '@angular/core';
import { HomeTabs } from '@local/client-contracts';
import { PopupRef, PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuItem, ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { WorkspacesService } from 'src/app/bar/services';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { cutText } from '@local/ts-infra';
import { AppPopupComponent, AppPopupData } from 'src/app/bar/components/app-popup/app-popup.component';
import { HeaderWidgetService } from '../../services/header-widget.service';
import { cloneDeep } from 'lodash';
import { filter } from 'rxjs';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { MoveWidgetToTabSelect } from 'src/app/bar/components/selected-item-popup/models/move-widget-to-tab-select';

@UntilDestroy()
@Component({
  selector: 'header-widget',
  templateUrl: './header-widget.component.html',
  styleUrls: ['./header-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderWidgetComponent extends WidgetBaseComponent<HomeTabs.HeaderWidget> implements OnInit {
  readonly MAX_LENGTH_HEADER = 85;
  private WarningPopupRef: PopupRef<AppPopupComponent, AppPopupData>;
  private newName: string;
  private _editMode: boolean;

  get editMode(): boolean {
    return this._editMode;
  }

  set editMode(value: boolean) {
    if (!this.canEdit) return;
    this.widgetsService.editMode = this._editMode = value;
  }

  constructor(
    protected elementRef: ElementRef,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected resultCommandService: ResultCommandService,
    protected ngZone: NgZone,
    protected session: SessionService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected routerService: RouterService,
    protected popupService: PopupService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService,
    protected headerWidgetService: HeaderWidgetService,
    protected collectionsService: CollectionsService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
  }

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent) {
    event.preventDefault();
    if (this.canEdit) {
      this.openContextMenu(event);
    }
  }

  async loadData() {
    this.headerWidgetService
      .get$(this.settings.id)
      .pipe(
        untilDestroyed(this),
        filter((w) => !!w)
      )
      .subscribe((widget) => {
        this.settings = cloneDeep(widget);
        this.isLoading = false;
        if (!this.settings.alignTo) {
          this.settings.alignTo = 'left';
        }
        if (!this.settings.title) {
          this.settings.title = 'Untitled';
        }
        if (this.widgetsService.published?.id === this.settings.id && this.widgetsService.published.firstTime) {
          this.editMode = true;
          this.newName = this.settings.title;
          this.widgetsService.published = { ...this.settings, firstTime: false };
        }
      });
  }

  buildContextMenuItems(): ContextMenuItem[] {
    const items: ContextMenuItem[] = [
      {
        id: 'edit',
        text: 'Edit',
        icon: { type: 'font-icon', value: 'icon-edit' },
      },
      {
        id: 'text_align',
        text: `Align ${this.settings?.alignTo === 'left' ? 'Center' : 'Left'}`,
        icon: { type: 'font-icon', value: `icon-align-${this.settings?.alignTo === 'left' ? 'center' : 'left'}` },
      },
      {
        id: 'make_a_copy',
        text: 'Make a copy',
        icon: { type: 'font-icon', value: 'icon-duplicate' },
      },
      {
        id: 'move-to-tab',
        text: 'Move to Tab',
        icon: { type: 'font-icon', value: 'icon-arrow-right-square' },
      },
      {
        id: 'delete',
        text: 'Delete',
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
    ];
    return items;
  }

  async handleContextMenuCommand(command: ContextMenuItem) {
    switch (command.id) {
      case 'edit':
        this.editMode = true;
        break;
      case 'text_align':
        await this.updateTextAlign();
        break;
      case 'delete':
        this.openDeletePostWarningPopup();
        break;
      case 'make_a_copy':
        await this.headerWidgetService.createHeaderWidget(this.settings.tabId, this.settings.title, this.settings.alignTo);
        break;
      case 'move-to-tab':
        this.collectionsService.openSelectPopup$.next({
          data: {
            selectedTabId: this.settings.tabId,
            createNewOption: true,
            toasterContent: `Header ${cutText(this.settings.title, 40)} was moved to {*} successfully`,
            widget: this.settings,
            moveToFirst: true,
          } as MoveWidgetToTabSelect,
          type: 'move-header-widget',
        });
        break;
    }
  }

  async renameHeader() {
    if (!this.canEdit) return;
    this.editMode = false;
    if (this.newName === this.settings.title) return;
    this.settings.title = !this.newName || this.newName === '' ? 'Untitled' : this.newName;
    return this.widgetsService.update(this.settings.tabId, [this.settings as HomeTabs.HeaderWidget]);
  }

  onChangeName(val: string) {
    this.newName = val;
  }

  private async updateTextAlign() {
    this.settings.alignTo = this.settings.alignTo === 'left' ? 'center' : 'left';
    return this.widgetsService.update(this.settings.tabId, [this.settings as HomeTabs.HeaderWidget]);
  }

  private openDeletePostWarningPopup() {
    this.WarningPopupRef?.destroy();
    const popupData: AppPopupData = {
      message: 'This will affect all members it is shared with.',
      showButtons: true,
      content: {
        title: 'Are you sure you want to <br> delete this Header?',
        secondaryButton: 'Cancel',
        primaryButton: 'Delete',
      },
      messageStyle: { fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: '18px' },
    };
    this.WarningPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>('center', AppPopupComponent, popupData, {
      position: 'center',
    });
    this.WarningPopupRef.compInstance.primaryButton.subscribe(async () => {
      this.WarningPopupRef.destroy();
      this.WarningPopupRef = null;
      await this.widgetsService.delete(this.settings.tabId, this.settings.id);
    });
    this.WarningPopupRef.compInstance.secondaryButton.subscribe(() => {
      this.WarningPopupRef.destroy();
      this.WarningPopupRef = null;
    });
  }
}
